import React, { useState, useEffect } from 'react';
import { 
  BarChart3, TrendingUp, Eye, Calculator, Clock, Users, Euro, 
  RefreshCw, Calendar, ArrowUp, ArrowDown, Activity
} from 'lucide-react';
import { Button } from './ui/button';
import { analyticsAPI } from '../services/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const AnalyticsAdminTab = () => {
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [useCaseStats, setUseCaseStats] = useState(null);
  const [roiStats, setRoiStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ucStats, roiData, trendsData, activity] = await Promise.all([
        analyticsAPI.getUseCaseStats(period),
        analyticsAPI.getROIStats(period),
        analyticsAPI.getTrends(period === 'today' ? 1 : period === 'week' ? 7 : period === 'month' ? 30 : 90),
        analyticsAPI.getRecentActivity(15)
      ]);
      
      setUseCaseStats(ucStats);
      setRoiStats(roiData);
      setTrends(trendsData);
      setRecentActivity(activity?.activities || []);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  const formatCurrency = (num) => {
    if (!num) return '€0';
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(num);
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m geleden`;
    if (diffHours < 24) return `${diffHours}u geleden`;
    return `${diffDays}d geleden`;
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, trend, color = 'blue' }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="text-3xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500 mt-1">{title}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
    </div>
  );

  if (loading && !useCaseStats) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Period Filter */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
          <p className="text-gray-500 mt-1">Bekijk statistieken over use case views en ROI berekeningen</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Vandaag</SelectItem>
              <SelectItem value="week">Deze week</SelectItem>
              <SelectItem value="month">Deze maand</SelectItem>
              <SelectItem value="all">Alles</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Eye}
          title="Totaal Page Views"
          value={formatNumber(useCaseStats?.total_views || 0)}
          subtitle={`${useCaseStats?.use_cases?.length || 0} use cases bekeken`}
          color="blue"
        />
        <StatCard
          icon={Calculator}
          title="ROI Berekeningen"
          value={formatNumber(roiStats?.total_calculations || 0)}
          subtitle={`${roiStats?.use_cases?.length || 0} calculators gebruikt`}
          color="green"
        />
        <StatCard
          icon={Euro}
          title="Totaal Berekende Besparing"
          value={formatCurrency(roiStats?.total_savings_calculated || 0)}
          subtitle={`Gem. ${formatCurrency(roiStats?.avg_savings_per_calculation || 0)} per berekening`}
          color="amber"
        />
        <StatCard
          icon={Clock}
          title="Gem. Bespaarde Uren"
          value={formatNumber(roiStats?.avg_saved_hours_per_calculation || 0)}
          subtitle="Per ROI berekening"
          color="purple"
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Use Cases by Views */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Meest Bekeken Use Cases</h3>
          </div>
          
          {useCaseStats?.use_cases?.length > 0 ? (
            <div className="space-y-3">
              {useCaseStats.use_cases.slice(0, 8).map((uc, index) => (
                <div key={uc.use_case_id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{uc.title}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                        <span>{uc.unique_visitors || 0} unieke bezoekers</span>
                        {uc.avg_time_spent_seconds > 0 && (
                          <span>~{Math.round(uc.avg_time_spent_seconds / 60)}min</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-blue-600">{uc.total_views}</p>
                    <p className="text-xs text-gray-400">views</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Nog geen data beschikbaar</p>
          )}
        </div>

        {/* Top Use Cases by ROI Calculations */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calculator className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">Top ROI Calculators</h3>
          </div>
          
          {roiStats?.use_cases?.length > 0 ? (
            <div className="space-y-3">
              {roiStats.use_cases.slice(0, 8).map((uc, index) => (
                <div key={uc.use_case_id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 text-xs font-bold flex items-center justify-center">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{uc.title}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                        <span>{uc.unique_users || 0} unieke gebruikers</span>
                        <span>Gem. {formatCurrency(uc.avg_savings || 0)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-600">{uc.total_calculations}</p>
                    <p className="text-xs text-gray-400">berekeningen</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Nog geen data beschikbaar</p>
          )}
        </div>
      </div>

      {/* Trends Chart (Simplified) */}
      {trends && (trends.views_trend?.length > 0 || trends.roi_trend?.length > 0) && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Activiteit Trend</h3>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Views Trend */}
            <div>
              <p className="text-sm font-medium text-gray-600 mb-2">Page Views per dag</p>
              <div className="flex items-end gap-1 h-32">
                {trends.views_trend.slice(-14).map((day, i) => (
                  <div
                    key={day.date}
                    className="flex-1 bg-blue-200 rounded-t hover:bg-blue-400 transition-colors"
                    style={{ height: `${Math.max(8, (day.views / Math.max(...trends.views_trend.map(d => d.views), 1)) * 100)}%` }}
                    title={`${day.date}: ${day.views} views`}
                  />
                ))}
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{trends.views_trend[0]?.date?.slice(5)}</span>
                <span>{trends.views_trend[trends.views_trend.length - 1]?.date?.slice(5)}</span>
              </div>
            </div>
            
            {/* ROI Trend */}
            <div>
              <p className="text-sm font-medium text-gray-600 mb-2">ROI Berekeningen per dag</p>
              <div className="flex items-end gap-1 h-32">
                {trends.roi_trend.slice(-14).map((day, i) => (
                  <div
                    key={day.date}
                    className="flex-1 bg-green-200 rounded-t hover:bg-green-400 transition-colors"
                    style={{ height: `${Math.max(8, (day.calculations / Math.max(...trends.roi_trend.map(d => d.calculations), 1)) * 100)}%` }}
                    title={`${day.date}: ${day.calculations} berekeningen`}
                  />
                ))}
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{trends.roi_trend[0]?.date?.slice(5)}</span>
                <span>{trends.roi_trend[trends.roi_trend.length - 1]?.date?.slice(5)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="h-5 w-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Recente Activiteit</h3>
        </div>
        
        {recentActivity.length > 0 ? (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {recentActivity.map((activity, index) => (
              <div key={`${activity.type || 'act'}-${activity.timestamp || activity.date || index}`} className="flex items-center justify-between p-3 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${activity.type === 'view' ? 'bg-blue-100' : 'bg-green-100'}`}>
                    {activity.type === 'view' ? (
                      <Eye className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Calculator className="h-4 w-4 text-green-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      {activity.type === 'view' ? 'Bekeken:' : 'ROI berekend:'} {activity.use_case_title}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {activity.user_email ? (
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {activity.user_email}
                        </span>
                      ) : (
                        <span className="text-gray-400">Anoniem</span>
                      )}
                      {activity.type === 'view' && activity.time_spent_seconds > 0 && (
                        <span>~{Math.round(activity.time_spent_seconds / 60)}min</span>
                      )}
                      {activity.type === 'roi_calculation' && activity.results?.savings > 0 && (
                        <span className="text-green-600 font-medium">
                          {formatCurrency(activity.results.savings)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <span className="text-xs text-gray-400">
                  {formatTimeAgo(activity.created_at)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">Nog geen recente activiteit</p>
        )}
      </div>
    </div>
  );
};

export default AnalyticsAdminTab;
